import { useEffect, useId, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Dialog, DialogContent, DialogTitle } from "../shared/modal";
import { LoadingContent } from "../shared/loading-content";
import { SharedIcon } from "../shared/shared-icon";
import { Checkbox } from "../shared/checkbox";
import { useColours } from "../../../js/api/member-profile/colours";
import { useDebouncedValue } from "./lib/use-debounced-value";

/**
 * The "Colours used" picker: one modal serving both of the design's entry pills.
 *
 * `source` chooses which:
 *
 *   "search"  free-text across every published chip, by name, display name or code
 *   "saved"   the member's own saved colours
 *
 * Both answer in the same shape and are painted with the same grid, which is the whole reason the
 * server exposes them through one action.
 *
 * THE SHELL IS THE SITE'S SHELL. `ADD TO COLLECTION`, `CREATE NEW COLLECTION` and `UPLOAD A PHOTO`
 * are one component in the design (sprint2-design-spec.md §2.5) and this is a fourth instance of
 * it, not a fourth invention: `DialogContent` brings the 32/48px radius, the drop shadow and the
 * circular ✕ that overlaps the top-right corner from outside, and the markup below adds only the
 * three things the shell does not know about — a centred condensed-uppercase title, a scrolling
 * body, and the `Cancel` / dark primary footer pair. Those are `.button` and `.button.grey`, the
 * site's own pills; `.cta` is not a class that exists anywhere in the SCSS tree.
 *
 * THE GRID IS THE SITE'S COLOUR GRID. Rounded swatch, name, code, centred — the treatment
 * Views/Partials/ColourCategory/ColourList.cshtml and modules/colour-chip.scss establish, so a
 * member meets the same object here that they met while browsing.
 *
 * WHY THE TILE IS THE CONTROL, NOT A CHECKBOX BESIDE IT. The whole tile is one
 * `<button role="checkbox">`: the target is the thing being chosen rather than a 32px box floating
 * over the corner of it, the swatch itself carries the selected ring, and assistive technology
 * gets the state from `aria-checked` on the same element that holds the name and the code. A
 * colour already on the project is `aria-disabled` rather than `disabled` so it stays reachable
 * and still announces WHY it cannot be chosen.
 *
 * CONTROLLED, NOT A STORE. Every other modal in this repository holds `isModalOpen` in a zustand
 * store, because those are mounted once at the root of #main-react and triggered from anywhere on
 * the page, including from markup React does not own. This one is opened by the field beside it
 * and hands its selection straight back to it, so a module-level singleton would buy nothing and
 * cost correctness: two colour fields on one page would share one open flag and one selection.
 *
 * It also cannot use shared/error-content.jsx, which reads its wording from MainReactContext. The
 * member profile editor mounts its own root (member-profile-editor.jsx) with its own
 * QueryClientProvider and NO MainReactProvider, so useMainReact() there returns undefined and
 * destructuring it throws. The error state below is local for that reason.
 *
 * Radix owns the keyboard: focus trapping, Escape and the overlay click are all its behaviour and
 * nothing here overrides them. The one thing it is asked for is where focus lands on open — see
 * `onOpenAutoFocus` below, which defers to Radix whenever there is no search box to land in.
 *
 * Copy is a prop, from the same dictionary-backed object ZoneEditor.cshtml builds for the rest of
 * the editor. It is optional only so the existing call site keeps working while the dictionary
 * entries are wired; DEFAULT_COPY is the fallback, not the source of truth.
 */
export function ColourPickerModal({
  isOpen,
  source,
  chosenKeys,
  settings,
  onClose,
  onConfirm,
  copy = {},
}) {
  const isSaved = source === "saved";
  const text = { ...DEFAULT_COPY, ...copy };

  const [term, setTerm] = useState("");
  const [selected, setSelected] = useState([]);

  const searchInput = useRef(null);
  const searchId = `${useId()}-colour-picker-search`;

  // Each opening starts clean: the modal adds to what the field holds, it does not re-state it.
  useEffect(() => {
    if (isOpen) {
      setTerm("");
      setSelected([]);
    }
  }, [isOpen, source]);

  // The settled term, not every keystroke — it is the query key, so debouncing here is what makes
  // a search cancel the request it replaced rather than race it.
  const debouncedTerm = useDebouncedValue(term, DEBOUNCE_MS);

  const minLength = settings.colourSearchMinLength;

  const { data, isLoading, isFetching, isError, refetch } = useColours({
    term: debouncedTerm,
    saved: isSaved,
    minLength,
    enabled: isOpen,
  });

  const colours = useMemo(() => data ?? [], [data]);

  // A colour already on the project is shown ticked and locked rather than hidden: a member
  // looking for one they have already added needs to see that it is there, not wonder why their
  // search cannot find it.
  const isAlreadyChosen = (colour) => chosenKeys.includes(colour.key);

  const isSelected = (colour) => selected.some((chosen) => chosen.key === colour.key);

  const toggle = (colour) =>
    setSelected((current) =>
      current.some((chosen) => chosen.key === colour.key)
        ? current.filter((chosen) => chosen.key !== colour.key)
        : [...current, colour]
    );

  const isBelowMinimum = !isSaved && debouncedTerm.trim().length < minLength;
  const isEmpty = !isLoading && !isError && !isBelowMinimum && colours.length === 0;
  const hasResults = !isLoading && !isError && colours.length > 0;

  // Typing again after a result set has painted: the grid stays put and dims rather than being
  // torn down for a spinner, so the modal does not flash on every pause in typing. The pending
  // half of the test covers the debounce window, when nothing is in flight yet but the results on
  // screen already answer a term the member has moved on from.
  const isRefreshing = hasResults && (isFetching || term.trim() !== debouncedTerm.trim());

  // A full page means the server had more to give. There is no "next page" — narrowing the term
  // is the better answer over ~2,600 chips — so the member is told rather than left believing
  // these are all the matches there are.
  const isTruncated = !isSaved && colours.length >= settings.colourPickerPageSize;

  const title = isSaved ? text.colourPickerSavedTitle : text.colourPickerSearchTitle;

  const confirmLabel =
    selected.length === 0
      ? text.colourPickerConfirm
      : format(
          selected.length === 1 ? text.colourPickerConfirmOne : text.colourPickerConfirmMany,
          selected.length
        );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="react-modal-content--wide member-profile-colours-modal"
        data-testid="colour-picker-modal"
        // The dialog is titled but not described, and Radix warns about the missing description
        // rather than letting it pass silently. Saying so explicitly is the documented answer.
        aria-describedby={undefined}
        // Where focus lands. The search box is the first thing a member does on the search source,
        // so opening with the caret already in it saves a Tab; on the saved source there is no box
        // and Radix's own choice — the panel itself — is the right one, so nothing is prevented.
        onOpenAutoFocus={(event) => {
          if (!searchInput.current) {
            return;
          }

          event.preventDefault();
          searchInput.current.focus();
        }}
      >
        <div className="member-profile-colours-modal__header">
          <DialogTitle className="member-profile-colours-modal__title">{title}</DialogTitle>

          {!isSaved && (
            <div className="member-profile-colours-modal__search">
              <label className="member-profile-colours-modal__sr" htmlFor={searchId}>
                {text.colourPickerSearchLabel}
              </label>

              <SharedIcon
                iconName="#sprite-search-icon"
                className="member-profile-colours-modal__search-icon"
                aria-hidden="true"
              />

              <input
                id={searchId}
                ref={searchInput}
                type="search"
                className="member-profile-colours-modal__search-input"
                data-testid="colour-picker-search"
                autoComplete="off"
                placeholder={text.colourPickerSearchPlaceholder}
                value={term}
                onChange={(event) => setTerm(event.target.value)}
              />

              {/* The native ✕ on type="search" is Chromium-only and unstyleable, so it is hidden
                  in the SCSS and this is the affordance in its place — present only when there is
                  something to clear, and returning focus to the box it just emptied. */}
              {term.length > 0 && (
                <button
                  type="button"
                  className="member-profile-colours-modal__search-clear"
                  data-testid="colour-picker-search-clear"
                  onClick={() => {
                    setTerm("");
                    searchInput.current?.focus();
                  }}
                >
                  <SharedIcon iconName="#sprite-icon-cross" className="icon" aria-hidden="true" />

                  <span className="member-profile-colours-modal__sr">
                    {text.colourPickerClearSearch}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className="member-profile-colours-modal__main" aria-busy={isLoading || isRefreshing}>
          {/* How many colours came back, for a screen reader that cannot see the grid repaint.
              Polite, and outside every conditional branch so it is present before it has anything
              to say — a live region added to the DOM already populated is not announced. */}
          <p className="member-profile-colours-modal__sr" role="status">
            {hasResults ? format(text.colourPickerResultsCount, colours.length) : ""}
          </p>

          {isBelowMinimum && (
            <PickerMessage
              icon="#sprite-search-icon"
              data-testid="colour-picker-prompt"
              // The number is the configured minimum, never a literal — it is the same one the
              // server applies, so this line cannot describe a gate that is not there.
              title={format(text.colourPickerPrompt, minLength)}
            />
          )}

          {isLoading && !isBelowMinimum && (
            <>
              <LoadingContent />

              <p className="member-profile-colours-modal__sr" role="status">
                {text.colourPickerLoading}
              </p>
            </>
          )}

          {isError && (
            <PickerMessage
              icon="#sprite-alert-circle"
              data-testid="colour-picker-error"
              title={text.colourPickerError}
            >
              <button type="button" className="button small" onClick={() => refetch()}>
                {text.colourPickerRetry}
              </button>
            </PickerMessage>
          )}

          {isEmpty && (
            <PickerMessage
              icon={isSaved ? "#sprite-heart" : "#sprite-search-icon"}
              data-testid="colour-picker-empty"
              title={isSaved ? text.colourPickerEmptySaved : text.colourPickerEmptySearch}
              hint={
                isSaved ? text.colourPickerEmptySavedHint : text.colourPickerEmptySearchHint
              }
            />
          )}

          {hasResults && (
            <ul
              className={clsx("member-profile-colours-modal__grid", {
                "member-profile-colours-modal__grid--refreshing": isRefreshing,
              })}
              data-testid="colour-picker-grid"
            >
              {colours.map((colour) => {
                const alreadyChosen = isAlreadyChosen(colour);
                const checked = alreadyChosen || isSelected(colour);

                return (
                  <li key={colour.key} className="member-profile-colours-modal__item">
                    {/*
                      SWATCH, NAME, CODE, THEN A CHECKBOX UNDERNEATH — the same shape the site's
                      own colour grids use (see my-saved-colors/color-item.jsx, which is the
                      "ADD TO COLLECTION" picker). The checkbox is the control; the tile is not a
                      button. A tick drawn on the swatch instead of a checkbox looked like a
                      different component from the rest of the site.

                      The checkbox carries its own label because the name and code beside it are
                      separate elements, and "checkbox, checked" with no name tells a screen
                      reader user nothing about which colour they just picked.
                    */}
                    <div
                      className={clsx("member-profile-colours-modal__option", {
                        "member-profile-colours-modal__option--selected": checked,
                        "member-profile-colours-modal__option--locked": alreadyChosen,
                      })}
                      data-testid="colour-picker-option"
                    >
                      <span
                        className="member-profile-colours-modal__swatch"
                        style={{ backgroundColor: colour.rgb }}
                      />

                      <span className="member-profile-colours-modal__name">{colour.name}</span>
                      <span className="member-profile-colours-modal__code">{colour.code}</span>

                      <Checkbox
                        className="shadcn-checkbox member-profile-colours-modal__checkbox"
                        data-testid="colour-picker-option-checkbox"
                        checked={checked}
                        disabled={alreadyChosen}
                        aria-label={
                          alreadyChosen
                            ? `${colour.name}, ${colour.code}, ${text.colourPickerAlreadyAdded}`
                            : `${colour.name}, ${colour.code}`
                        }
                        onCheckedChange={() => !alreadyChosen && toggle(colour)}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {isTruncated && (
            <p className="member-profile-colours-modal__note" data-testid="colour-picker-truncated">
              {format(text.colourPickerTruncated, settings.colourPickerPageSize)}
            </p>
          )}
        </div>

        <div className="member-profile-colours-modal__footer">
          <button
            type="button"
            className="button transparent member-profile-colours-modal__cancel"
            data-testid="colour-picker-cancel"
            onClick={onClose}
          >
            {text.colourPickerCancel}
          </button>

          <button
            type="button"
            className="button grey member-profile-colours-modal__confirm"
            data-testid="colour-picker-confirm"
            disabled={selected.length === 0}
            onClick={() => onConfirm(selected)}
          >
            {confirmLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The modal's non-grid states — nothing to search for yet, nothing found, nothing loaded.
 *
 * One shape for all three so they do not drift into three different silhouettes: an outlined
 * circular icon, a line saying what happened, and an optional second line saying what to do about
 * it. `children` is the recovery control, which only the error state has.
 */
function PickerMessage({ icon, title, hint, children, ...rest }) {
  return (
    <div className="member-profile-colours-modal__message" {...rest}>
      <span className="member-profile-colours-modal__message-icon" aria-hidden="true">
        <SharedIcon iconName={icon} className="icon" />
      </span>

      <p className="member-profile-colours-modal__message-title">{title}</p>

      {hint && <p className="member-profile-colours-modal__message-hint">{hint}</p>}

      {children}
    </div>
  );
}

/**
 * `{0}`-style placeholders, the same convention the rest of the editor's copy uses (see the remove
 * control in colour-field.jsx) and the one Umbraco dictionary values can carry.
 */
const format = (template, ...values) =>
  values.reduce(
    (result, value, index) => result.split(`{${index}}`).join(String(value)),
    template ?? ""
  );

/**
 * English fallbacks, so the modal is never blank while the dictionary entries are being wired.
 *
 * These are NOT where the wording lives. Every key here is expected to arrive on the `copy` prop
 * that ZoneEditor.cshtml builds from the dictionary; the object below is what shows if one is
 * missing, in the same way copy.addColour has a home in the dictionary and colour-field.jsx just
 * prints it.
 */
const DEFAULT_COPY = {
  colourPickerSearchTitle: "Add a colour",
  colourPickerSavedTitle: "Add saved colours",
  colourPickerSearchLabel: "Search colours by name or code",
  colourPickerSearchPlaceholder: "Search by colour name or code",
  colourPickerClearSearch: "Clear search",
  colourPickerPrompt: "Type at least {0} characters to search for a colour",
  colourPickerLoading: "Loading colours",
  colourPickerError: "Sorry, colours could not be loaded just now",
  colourPickerRetry: "Try again",
  colourPickerEmptySearch: "No colours match that search",
  colourPickerEmptySearchHint: "Check the spelling, or try a shorter search",
  colourPickerEmptySaved: "You have not saved any colours yet",
  colourPickerEmptySavedHint: "Colours you save while browsing will appear here",
  colourPickerTruncated: "Showing the first {0} matches. Keep typing to narrow them down.",
  colourPickerAlreadyAdded: "already added",
  colourPickerResultsCount: "{0} colours found",
  colourPickerCancel: "Cancel",
  colourPickerConfirm: "Add colours",
  colourPickerConfirmOne: "Add {0} colour",
  colourPickerConfirmMany: "Add {0} colours",
};

/**
 * How long the search box waits for a pause before asking.
 *
 * Not a setting: it is a feel, not a limit, and nothing outside the dev team would express an
 * opinion about it in milliseconds. Every number that describes a RULE — the page size, the
 * minimum term length — comes from settings, which come from appsettings.
 */
const DEBOUNCE_MS = 300;
